import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBoundStore } from "store";
import { eligibilityService } from "eligibility/services/EligibilityService";

export const eligibilityKeys = {
  all: ["eligibility"] as const,
  formats: (t?: string, o?: string) =>
    [...eligibilityKeys.all, "formats", t ?? "?", o ?? "?"] as const,
  checks: (t?: string, o?: string, projectId?: string) =>
    [...eligibilityKeys.all, "checks", t ?? "?", o ?? "?", projectId ?? "all"] as const,
};

export function useBillFormats(organizationId?: string) {
  const { tenant } = useBoundStore();
  return useQuery({
    queryKey: eligibilityKeys.formats(tenant?.id, organizationId),
    queryFn: () => eligibilityService.getFormats(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId,
    staleTime: 60 * 60_000,
  });
}

export function useEligibilityChecks(organizationId?: string, projectId?: string) {
  const { tenant } = useBoundStore();
  return useQuery({
    queryKey: eligibilityKeys.checks(tenant?.id, organizationId, projectId),
    queryFn: () =>
      eligibilityService.listChecks(tenant!.id, organizationId as string, projectId),
    enabled: !!tenant?.id && !!organizationId,
  });
}

export function useCreateEligibilityCheck(organizationId?: string) {
  const { tenant } = useBoundStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ billFormat, files }: { billFormat: string; files: File[] }) =>
      eligibilityService.createCheck(
        tenant!.id,
        organizationId as string,
        billFormat,
        files
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eligibilityKeys.checks(tenant?.id, organizationId),
      });
    },
  });
}

export function useAttachEligibilityCheck(organizationId?: string) {
  const { tenant } = useBoundStore();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string }) =>
      eligibilityService.attachToProject(
        tenant!.id,
        organizationId as string,
        id,
        projectId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eligibilityKeys.checks(tenant?.id, organizationId),
      });
    },
  });
}
